import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  Client,
  Conversation,
  ConversationUpdateReason,
  Message,
  Paginator,
  Participant,
  User,
} from '@twilio/conversations';
import { Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { promiseWithCatch, subjectsComplete, unsubscribeAll } from 'src/app/common/app.helpers';
import AppSettings from 'src/app/common/app.settings';
import { equalUserInfos, UserInfo } from 'src/app/interfaces';
import {
  isGroupConversation,
  isNotificationChannel,
  isPrivateConversation,
  messageBelongsToNotificationChannel,
  sortConversationsByLastMessageDateTime,
} from '../../helpers/chat.helpers';
import { ConversationsAdapter } from '../../helpers/conversations.adapter';
import { TwilioMessagesAdapter } from '../../helpers/twilio-message.adapter';
import { computeConversationEffectiveTitle, getTwilioMessageAuthorUser } from '../../helpers/twilio-message.helpers';
import { ChatEngineChannelsService } from '../../interfaces/chat-engine/chat-engine-channels.service';
import { ChatEngineLifeСycleService } from '../../interfaces/chat-engine/chat-engine-lifecycle-service';
import {
  ChatChannelType,
  ChatConversation,
  ChatEngineHandle,
  ChatMessage,
  ChatParticipantInfo,
  ChatTraceData,
} from '../../interfaces/chat-model.interface';
import { TwilioChatEngineHandle } from './twilio-chat-engine.handle';
import { TwilioClientAccessService } from './twilio-client-access-service';
import {
  TWILIO_ERROR_CONVERSATION_NOT_FOUND,
  TwilioErrorService,
  TwilioEventHandlers,
  TwilioInternalOperations,
} from './twilio-error.service';
import { TwilioUsersService } from './twilio-users.service';
import { TwilioConversationUpdated, TwilioMessageUpdated, TwilioUserAttributes } from './twilio.interfaces';

@Injectable()
export class TwilioChannelsService extends ChatEngineChannelsService implements OnDestroy {
  public get initialConversationsListLoaded(): Observable<ChatConversation[]> {
    return this.initialConversationsListLoaded$.asObservable();
  }

  public get conversationsListChanged(): Observable<ChatConversation[]> {
    return this.conversationsListChanged$.asObservable();
  }

  public get conversationJoined(): Observable<ChatConversation> {
    return this.conversationJoined$.asObservable();
  }

  public get conversationRemoved(): Observable<ChatConversation> {
    return this.conversationRemoved$.asObservable();
  }

  public get conversationRenamed(): Observable<ChatConversation> {
    return this.conversationRenamed$.asObservable();
  }

  public get participantJoined(): Observable<ChatParticipantInfo> {
    return this.participantJoined$.asObservable();
  }

  public get participantLeft(): Observable<ChatParticipantInfo> {
    return this.participantLeft$.asObservable();
  }

  private conversationsByUniqueName: Map<string, ChatConversation> = new Map();
  private conversationsInProgress: Map<string, Promise<ChatConversation>> = new Map();
  private loadedConversations: ChatConversation[] = [];
  private loadingInitialConversations = false;

  private initialConversationsListLoaded$: Subject<ChatConversation[]> = new ReplaySubject(1);
  private conversationsListChanged$: Subject<ChatConversation[]> = new ReplaySubject(1);
  private conversationJoined$: Subject<ChatConversation> = new Subject();
  private conversationRemoved$: Subject<ChatConversation> = new Subject();
  private conversationRenamed$: Subject<ChatConversation> = new Subject();
  private participantJoined$: Subject<ChatParticipantInfo> = new Subject();
  private participantLeft$: Subject<ChatParticipantInfo> = new Subject();

  private engineReadinessSubscription$: Subscription | null = null;
  private otherSubscriptions$: Subscription[] = [];

  private chatEngineLifeCycleService = inject(ChatEngineLifeСycleService);
  private twilioClientAccessService = inject(TwilioClientAccessService);
  private twilioErrorService = inject(TwilioErrorService);
  private twilioUsersService = inject(TwilioUsersService);
  private sessionService = inject(SessionService);

  public constructor() {
    super();
    this.subscribeToEngineReadinessChanged();
  }

  public ngOnDestroy(): void {
    if (this.engineReadinessSubscription$) {
      this.engineReadinessSubscription$.unsubscribe();
    }
    unsubscribeAll(this.otherSubscriptions$);
    subjectsComplete([
      this.initialConversationsListLoaded$,
      this.conversationsListChanged$,
      this.conversationJoined$,
      this.conversationRemoved$,
      this.conversationRenamed$,
      this.participantJoined$,
      this.participantLeft$,
    ]);
  }

  public async getConversationByUniqueName(channelUniqueName: string): Promise<ChatConversation> {
    const conversation: ChatConversation | undefined = this.conversationsByUniqueName.get(channelUniqueName);
    if (conversation) {
      return conversation;
    }

    const twilioConversation: Conversation = await this.extractTwilioConversation(channelUniqueName);
    return await this.twilioConversationToChatConversation(twilioConversation);
  }

  public async tryResolvingTwilioConversation(channelUniqueName: string): Promise<Conversation | null> {
    try {
      return await this.extractTwilioConversation(channelUniqueName);
    } catch (error) {
      if (
        (error as Error) &&
        (error as Error)['body'] &&
        ((error as Error)['body'] as Record<string, unknown>)['code'] === TWILIO_ERROR_CONVERSATION_NOT_FOUND
      ) {
        return null;
      } else {
        throw error;
      }
    }
  }

  public async resolveTwilioConversation(channelUniqueName: string): Promise<Conversation> {
    const twilioConversation: Conversation = await this.extractTwilioConversation(channelUniqueName);
    if (isNotificationChannel(twilioConversation)) {
      throw new Error('Unexpected notification channel in TwilioChannelsService');
    } else {
      // Ensure converted conversation is cached
      await this.twilioConversationToChatConversation(twilioConversation);
      return twilioConversation;
    }
  }

  public emitSortedConversationsList(initial?: boolean): void {
    const filteredConversations: ChatConversation[] = this.filterBlockedConversations(this.loadedConversations);
    const sortedConversations: ChatConversation[] = sortConversationsByLastMessageDateTime(filteredConversations);
    if (initial) {
      this.initialConversationsListLoaded$.next(sortedConversations);
    }
    this.conversationsListChanged$.next(sortedConversations);
  }

  private async extractTwilioConversation(channelUniqueName: string): Promise<Conversation> {
    const twilioClient: Client = this.twilioClientAccessService.getTwilioClient();
    return await twilioClient.getConversationByUniqueName(channelUniqueName);
  }

  private twilioConversationToChatConversation(twilioConversation: Conversation): Promise<ChatConversation> {
    const conversation: ChatConversation | undefined = this.conversationsByUniqueName.get(
      twilioConversation.uniqueName,
    );
    if (conversation) {
      return Promise.resolve(conversation);
    }

    const existingConversationBuildPromise: Promise<ChatConversation> | undefined = this.conversationsInProgress.get(
      twilioConversation.uniqueName,
    );
    if (existingConversationBuildPromise !== undefined) {
      return existingConversationBuildPromise;
    }

    const conversationBuildPromise: Promise<ChatConversation> = this.buildChatConversation(twilioConversation);
    this.conversationsInProgress.set(twilioConversation.uniqueName, conversationBuildPromise);

    return conversationBuildPromise.then((chatConversation: ChatConversation) => {
      this.conversationsByUniqueName.set(twilioConversation.uniqueName, chatConversation);
      this.conversationsInProgress.delete(twilioConversation.uniqueName);
      this.loadedConversations.push(chatConversation);
      if (!this.loadingInitialConversations) {
        this.emitSortedConversationsList();
      }
      return chatConversation;
    });
  }

  private async buildChatConversation(twilioConversation: Conversation): Promise<ChatConversation> {
    const lastMessage: Message | null =
      twilioConversation.lastMessage === undefined || twilioConversation.lastMessage.index === undefined
        ? null
        : (await twilioConversation.getMessages(1)).items[0];
    const blockedUserIds: string[] = this.twilioUsersService.currentUserAttributes.blocked || [];
    return await ConversationsAdapter.adaptTwilioConversationToConversation(
      this.sessionService.userId,
      twilioConversation,
      blockedUserIds,
      lastMessage,
    );
  }

  private subscribeToEngineReadinessChanged(): void {
    this.engineReadinessSubscription$ = this.chatEngineLifeCycleService.engineReadinessChanged.subscribe(
      // eslint-disable-next-line
      async (engineHandle: ChatEngineHandle) => {
        if (engineHandle.isValid()) {
          try {
            this.loadingInitialConversations = true;
            await this.loadInitialConversations(engineHandle as TwilioChatEngineHandle);

            this.subscribeToConversationEvents();
            this.subscribeToMessageEvents();
            this.subscribeToUserEvents();
          } catch (error) {
            this.twilioErrorService.onTwilioInternalOperationFailed(
              TwilioInternalOperations.CHANNELS_INITIALIZATION,
              error,
              {} as ChatTraceData,
            );
          } finally {
            this.loadingInitialConversations = false;
          }
        } else {
          unsubscribeAll(this.otherSubscriptions$);
          this.loadedConversations = [];
          this.loadingInitialConversations = false;
          this.conversationsByUniqueName.clear();
          this.conversationsInProgress.clear();

          // Restart replayable subjects that hold references to engine-dependent data
          subjectsComplete([this.conversationsListChanged$, this.initialConversationsListLoaded$]);
          this.conversationsListChanged$ = new ReplaySubject(1);
          this.initialConversationsListLoaded$ = new ReplaySubject(1);
        }
      },
    );
  }

  private async loadInitialConversations(engineHandle: TwilioChatEngineHandle): Promise<void> {
    const conversationsPaginator: Paginator<Conversation> = await engineHandle.client.getSubscribedConversations();
    if (!engineHandle.isValid()) {
      return;
    }

    const allSubscribedConversations: Conversation[] = await this.getSubscribedConversations(conversationsPaginator);
    if (!engineHandle.isValid()) {
      return;
    }

    const chatConversations: ChatConversation[] = await Promise.all(
      allSubscribedConversations
        .filter((conversation: Conversation) => !isNotificationChannel(conversation))
        .map((conversation: Conversation) => this.buildChatConversation(conversation)),
    );
    if (!engineHandle.isValid()) {
      return;
    }

    this.loadedConversations = chatConversations;
    this.loadedConversations.forEach((chatConversation: ChatConversation) =>
      this.conversationsByUniqueName.set(chatConversation.channel.channel_external_id, chatConversation),
    );
    this.emitSortedConversationsList(true);
  }

  private async getSubscribedConversations(conversationsPaginator: Paginator<Conversation>): Promise<Conversation[]> {
    if (conversationsPaginator.hasNextPage) {
      const nextConversationsPaginator: Paginator<Conversation> = await conversationsPaginator.nextPage();
      const tailConversations: Conversation[] = await this.getSubscribedConversations(nextConversationsPaginator);
      return conversationsPaginator.items.concat(tailConversations);
    } else {
      return conversationsPaginator.items;
    }
  }

  private subscribeToConversationEvents(): void {
    const twilioClient: Client = this.twilioClientAccessService.getTwilioClient();

    twilioClient.on('conversationJoined', (twilioConversation: Conversation) => {
      if (twilioConversation.state.current !== 'active' || isNotificationChannel(twilioConversation)) {
        return;
      }
      this.handleConversationJoined(twilioConversation).catch((error: unknown) => {
        this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.CHANNELS_CONVERSATION_JOINED, error, {
          channelUniqueName: twilioConversation.uniqueName,
        } as ChatTraceData);
      });
    });

    twilioClient.on('conversationUpdated', (event: TwilioConversationUpdated) => {
      if (event.updateReasons.some((reason: ConversationUpdateReason) => reason === 'state')) {
        if (event.conversation.state.current === 'active' && !isNotificationChannel(event.conversation)) {
          this.handleConversationJoined(event.conversation).catch((error: unknown) => {
            this.twilioErrorService.onTwilioEventHandlerFailed(
              TwilioEventHandlers.CHANNELS_CONVERSATION_UPDATED,
              error,
              {
                channelUniqueName: event.conversation.uniqueName,
              } as ChatTraceData,
            );
          });
        }
      } else if (event.updateReasons.some((reason: ConversationUpdateReason) => reason === 'friendlyName')) {
        promiseWithCatch(this.handleConversationRenamed(event.conversation));
      }
    });

    twilioClient.on('participantJoined', (participant: Participant) => {
      if (participant.conversation.state.current === 'active' && !isNotificationChannel(participant.conversation)) {
        this.handleConversationParticipantJoined(participant).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.CHANNELS_PARTICIPANT_JOINED, error, {
            channelUniqueName: participant.conversation.uniqueName,
            participantId: participant.identity,
          } as ChatTraceData);
        });
      }
    });

    twilioClient.on('participantLeft', (participant: Participant) => {
      if (participant.conversation.state.current === 'active' && !isNotificationChannel(participant.conversation)) {
        this.handleConversationParticipantLeft(participant).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.CHANNELS_PARTICIPANT_LEFT, error, {
            channelUniqueName: participant.conversation.uniqueName,
            participantId: participant.identity,
          } as ChatTraceData);
        });
      }
    });

    twilioClient.on('conversationRemoved', (twilioConversation: Conversation) => {
      if (!isNotificationChannel(twilioConversation)) {
        this.handleConversationRemoved(twilioConversation).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.CHANNELS_CONVERSATION_REMOVED, error, {
            channelUniqueName: twilioConversation.uniqueName,
          } as ChatTraceData);
        });
      }
    });
  }

  private async handleConversationJoined(twilioConversation: Conversation): Promise<void> {
    const existingConversation: ChatConversation | undefined = this.conversationsByUniqueName.get(
      twilioConversation.uniqueName,
    );
    if (existingConversation) {
      return;
    }

    const conversation: ChatConversation = await this.twilioConversationToChatConversation(twilioConversation);
    this.conversationJoined$.next(conversation);
  }

  private async handleConversationRenamed(twilioConversation: Conversation): Promise<void> {
    const conversation: ChatConversation = await this.twilioConversationToChatConversation(twilioConversation);
    const updatedTitle: string = computeConversationEffectiveTitle(
      twilioConversation.friendlyName,
      conversation.participatingUsers,
      this.sessionService.userId,
    );
    if (conversation.effectiveTitle !== updatedTitle) {
      conversation.effectiveTitle = updatedTitle;
      conversation.originalTitle = twilioConversation.friendlyName;
      this.conversationRenamed$.next(conversation);
      this.emitSortedConversationsList();
    }
  }

  private async handleConversationParticipantJoined(newParticipant: Participant): Promise<void> {
    const chatConversation: ChatConversation = await this.twilioConversationToChatConversation(
      newParticipant.conversation,
    );
    const userAttributes: TwilioUserAttributes = this.twilioUsersService.currentUserAttributes;
    const blockedUserIds: string[] = userAttributes.blocked || [];
    const newUser: User = await newParticipant.getUser();
    const newUserInfo: UserInfo = ConversationsAdapter.adaptTwilioUserToUserInfo(newUser, blockedUserIds);
    chatConversation.participatingUsers.push(newUserInfo);
    chatConversation.effectiveTitle = computeConversationEffectiveTitle(
      newParticipant.conversation.friendlyName,
      chatConversation.participatingUsers,
      this.sessionService.userId,
    );
    chatConversation.originalTitle = newParticipant.conversation.friendlyName;

    this.emitSortedConversationsList();

    this.participantJoined$.next({
      conversation: chatConversation,
      participant: newUserInfo,
    } as ChatParticipantInfo);
  }

  private async handleConversationParticipantLeft(leavingParticipant: Participant): Promise<void> {
    const chatConversation: ChatConversation = await this.twilioConversationToChatConversation(
      leavingParticipant.conversation,
    );
    const participantIndex: number = chatConversation.participatingUsers.findIndex(
      (u: UserInfo) => u.userId === leavingParticipant.identity,
    );
    if (participantIndex !== -1) {
      const leavingUserInfo: UserInfo = chatConversation.participatingUsers[participantIndex];
      chatConversation.participatingUsers.splice(participantIndex, 1);
      chatConversation.effectiveTitle = computeConversationEffectiveTitle(
        leavingParticipant.conversation.friendlyName,
        chatConversation.participatingUsers,
        this.sessionService.userId,
      );
      chatConversation.originalTitle = leavingParticipant.conversation.friendlyName;

      this.emitSortedConversationsList();

      this.participantLeft$.next({
        conversation: chatConversation,
        participant: leavingUserInfo,
      } as ChatParticipantInfo);
    }
  }

  private async handleConversationRemoved(twilioConversation: Conversation): Promise<void> {
    this.conversationsInProgress.delete(twilioConversation.uniqueName);

    const chatConversation: ChatConversation = await this.twilioConversationToChatConversation(twilioConversation);
    this.conversationsByUniqueName.delete(twilioConversation.uniqueName);

    const indexInLoaded: number = this.loadedConversations.indexOf(chatConversation);
    if (indexInLoaded === -1) {
      throw new Error('Inconsistent list of conversations');
    }
    this.loadedConversations.splice(indexInLoaded, 1);
    this.emitSortedConversationsList();
    this.conversationRemoved$.next(chatConversation);
  }

  private subscribeToMessageEvents(): void {
    const twilioClient: Client = this.twilioClientAccessService.getTwilioClient();

    twilioClient.on('messageAdded', (twilioMessage: Message) => {
      if (!messageBelongsToNotificationChannel(twilioMessage)) {
        this.onMessageAdded(twilioMessage).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.CHANNELS_MESSAGE_ADDED, error, {
            channelUniqueName: twilioMessage.conversation.uniqueName,
            messageId: twilioMessage.sid,
          } as ChatTraceData);
        });
      }
    });

    twilioClient.on('messageRemoved', (twilioMessage: Message) => {
      if (!messageBelongsToNotificationChannel(twilioMessage)) {
        this.onMessageRemoved(twilioMessage).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.CHANNELS_MESSAGE_REMOVED, error, {
            channelUniqueName: twilioMessage.conversation.uniqueName,
            messageId: twilioMessage.sid,
          } as ChatTraceData);
        });
      }
    });

    twilioClient.on('messageUpdated', (event: TwilioMessageUpdated) => {
      if (!messageBelongsToNotificationChannel(event.message)) {
        this.onMessageUpdated(event.message).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.CHANNELS_MESSAGE_UPDATED, error, {
            channelUniqueName: event.message.conversation.uniqueName,
            messageId: event.message.sid,
          } as ChatTraceData);
        });
      }
    });
  }

  private async onMessageAdded(twilioMessage: Message): Promise<void> {
    const chatConversation: ChatConversation = await this.twilioConversationToChatConversation(
      twilioMessage.conversation,
    );
    const convertedMessage: ChatMessage = await this.convertTwilioMessage(chatConversation, twilioMessage);
    this.changeConversationLastMessage(chatConversation, convertedMessage);
  }

  private async onMessageRemoved(twilioMessage: Message): Promise<void> {
    const chatConversation: ChatConversation = await this.twilioConversationToChatConversation(
      twilioMessage.conversation,
    );
    if (chatConversation.lastMessage && chatConversation.lastMessage.entity_id === twilioMessage.sid) {
      const paginator: Paginator<Message> = await twilioMessage.conversation.getMessages(1);
      const twilioPreLastMessage: Message | null = paginator.items.length > 0 ? paginator.items[0] : null;
      const convertedPreLastMessage: ChatMessage | null = twilioPreLastMessage
        ? await this.convertTwilioMessage(chatConversation, twilioPreLastMessage)
        : null;
      this.changeConversationLastMessage(chatConversation, convertedPreLastMessage);
    }
  }

  private async onMessageUpdated(twilioMessage: Message): Promise<void> {
    const chatConversation: ChatConversation = await this.twilioConversationToChatConversation(
      twilioMessage.conversation,
    );
    if (chatConversation.lastMessage && chatConversation.lastMessage.entity_id === twilioMessage.sid) {
      const convertedMessage: ChatMessage = await this.convertTwilioMessage(chatConversation, twilioMessage);
      this.changeConversationLastMessage(chatConversation, convertedMessage);
    }
  }

  private changeConversationLastMessage(conversation: ChatConversation, lastMessage: ChatMessage | null): void {
    conversation.lastMessage = lastMessage;

    this.emitSortedConversationsList();
  }

  private async convertTwilioMessage(conversation: ChatConversation, twilioMessage: Message): Promise<ChatMessage> {
    const userAttributes: TwilioUserAttributes = this.twilioUsersService.currentUserAttributes;
    const blockedUserIds: string[] = userAttributes.blocked || [];

    const messageSender: UserInfo = await getTwilioMessageAuthorUser(
      this.twilioClientAccessService.getTwilioClient(),
      twilioMessage,
      conversation.participatingUsers,
      blockedUserIds,
    );

    const channelParticipants: Participant[] = await twilioMessage.conversation.getParticipants();

    return TwilioMessagesAdapter.adaptTwilioMessageToCustomMessage(twilioMessage, channelParticipants, messageSender);
  }

  private subscribeToUserEvents(): void {
    this.otherSubscriptions$.push(
      this.twilioUsersService.otherUserInfoUpdated.subscribe((userInfo: UserInfo) =>
        this.updateUserInfoInConversations(userInfo),
      ),
    );

    this.otherSubscriptions$.push(
      this.twilioUsersService.blockedUsersListChanged.subscribe(() => this.onBlockedUserListsChanged()),
    );
  }

  private updateUserInfoInConversations(userInfo: UserInfo): void {
    let conversationsUpdated = false;

    this.loadedConversations.forEach((conversation: ChatConversation) => {
      if (this.updateUserInfoInConversation(conversation, userInfo)) {
        conversationsUpdated = true;
      }
    });

    if (conversationsUpdated) {
      this.emitSortedConversationsList();
    }
  }

  private updateUserInfoInConversation(conversation: ChatConversation, userInfo: UserInfo): boolean {
    const participant: UserInfo | undefined = conversation.participatingUsers.find(
      (p: UserInfo) => p.userId === userInfo.userId,
    );
    if (participant && !equalUserInfos(participant, userInfo)) {
      participant.userImage = userInfo.userImage;
      participant.userName = userInfo.userName;
      participant.userStatus = userInfo.userStatus;

      const users: UserInfo[] = conversation.participatingUsers.filter((p: UserInfo) => p.userName);
      const updatedTitle: string = computeConversationEffectiveTitle(
        conversation.originalTitle,
        users,
        this.sessionService.userId,
      );
      const updatedImageUrl: string = isPrivateConversation(conversation)
        ? ConversationsAdapter.privateConversationImage(users, this.sessionService.userId)
        : AppSettings.DEFAULT_USER_IMAGE;

      if (updatedTitle !== conversation.effectiveTitle || updatedImageUrl !== conversation.imageUrl) {
        conversation.effectiveTitle = updatedTitle;
        conversation.imageUrl = updatedImageUrl;
        return true;
      }
    }
    return false;
  }

  private onBlockedUserListsChanged(): void {
    // Not much to do yet, but re-emit conversations, which should re-activate filtering
    this.emitSortedConversationsList();
  }

  private filterBlockedConversations(conversations: ChatConversation[]): ChatConversation[] {
    const userAttributes: TwilioUserAttributes | null = this.twilioUsersService.currentUserAttributes;
    if (userAttributes && userAttributes.blocked) {
      const blockedUserIds: Set<string> = new Set(userAttributes.blocked);
      return conversations.filter((conversation: ChatConversation) => {
        return (
          conversation.channelType !== ChatChannelType.CONVERSATION ||
          isGroupConversation(conversation) ||
          !conversation.participatingUsers.some((participant: UserInfo) => blockedUserIds.has(participant.userId))
        );
      });
    } else {
      // Clone container to properly handle OnPush updates
      return [...conversations];
    }
  }
}
